import {
  canUseNativeChatEpochKeyStore,
  deleteNativeChatEpochKey,
  getNativeChatEpochKey,
  saveNativeChatEpochKey
} from '../utils/webviewBridge';

const DB_NAME = 'family-messenger-direct-chat-keys';
const DB_VERSION = 1;
const STORE_NAME = 'directChatKeys';
const OLD_EPOCH_KEY_GRACE_MS = 24 * 60 * 60 * 1000;

export type StoredDirectChatKey = {
  id: string;
  directChatId: string;
  epoch: number;
  rawKeyBase64: string;
  updatedAt: number;
};

function requestToPromise<T = unknown>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('byChatEpoch', ['directChatId', 'epoch'], { unique: true });
      }
    };
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => Promise<T>): Promise<T> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const done = new Promise<void>((doneResolve, doneReject) => {
      tx.oncomplete = () => doneResolve();
      tx.onerror = () => doneReject(tx.error);
      tx.onabort = () => doneReject(tx.error);
    });
    fn(store)
      .then((result) => {
        done
          .then(() => {
            db.close();
            resolve(result);
          })
          .catch((error) => {
            db.close();
            reject(error);
          });
      })
      .catch((error) => {
        try {
          tx.abort();
        } catch {
          // ignore
        }
        done.catch(() => undefined).finally(() => {
          db.close();
          reject(error);
        });
      });
  });
}

async function pruneDirectChatKeys(store: IDBObjectStore, directChatId: string, newestEpochHint?: number): Promise<void> {
  const rows = await requestToPromise(store.getAll()) as StoredDirectChatKey[];
  const keysForChat = rows.filter((row) => row.directChatId === directChatId);
  const newestEpoch = Math.max(newestEpochHint || 0, ...keysForChat.map((row) => row.epoch));
  const cutoff = Date.now() - OLD_EPOCH_KEY_GRACE_MS;
  for (const row of keysForChat) {
    if (row.epoch < newestEpoch && row.updatedAt < cutoff) {
      store.delete(row.id);
    }
  }
}

async function saveDirectChatKeyToIndexedDb(directChatId: string, epoch: number, rawKeyBase64: string): Promise<void> {
  await withStore('readwrite', async (store) => {
    await requestToPromise(store.put({ id: `${directChatId}:${epoch}`, directChatId, epoch, rawKeyBase64, updatedAt: Date.now() } as StoredDirectChatKey));
    await pruneDirectChatKeys(store, directChatId, epoch);
  });
}

async function getDirectChatKeyFromIndexedDb(directChatId: string, epoch: number): Promise<string | null> {
  return withStore('readwrite', async (store) => {
    await pruneDirectChatKeys(store, directChatId);
    const result = await requestToPromise(store.get(`${directChatId}:${epoch}`)) as StoredDirectChatKey | undefined;
    return result?.rawKeyBase64 || null;
  });
}

async function deleteDirectChatKeyFromIndexedDb(directChatId: string, epoch: number): Promise<void> {
  await withStore('readwrite', async (store) => {
    await requestToPromise(store.delete(`${directChatId}:${epoch}`));
  });
}

export async function saveDirectChatKey(directChatId: string, epoch: number, rawKeyBase64: string): Promise<void> {
  if (saveNativeChatEpochKey('direct', directChatId, epoch, rawKeyBase64)) {
    await deleteDirectChatKeyFromIndexedDb(directChatId, epoch);
    return;
  }
  await saveDirectChatKeyToIndexedDb(directChatId, epoch, rawKeyBase64);
}

export async function getDirectChatKey(directChatId: string, epoch: number): Promise<string | null> {
  const nativeKey = getNativeChatEpochKey('direct', directChatId, epoch);
  if (nativeKey) return nativeKey;

  const indexedDbKey = await getDirectChatKeyFromIndexedDb(directChatId, epoch);
  if (indexedDbKey && canUseNativeChatEpochKeyStore() && saveNativeChatEpochKey('direct', directChatId, epoch, indexedDbKey)) {
    await deleteDirectChatKeyFromIndexedDb(directChatId, epoch);
  }
  return indexedDbKey;
}

export async function deleteDirectChatKey(directChatId: string, epoch: number): Promise<void> {
  deleteNativeChatEpochKey('direct', directChatId, epoch);
  await deleteDirectChatKeyFromIndexedDb(directChatId, epoch);
}
